import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, FolderKanban, Trash2, ArrowRight, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function ProjectsPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", template_id: "tpl-blank" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get("/projects").then((r) => setProjects(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/templates").then((r) => setTemplates(r.data));
  }, [load]);

  useEffect(() => {
    if (params.get("new") === "1") {
      setOpen(true);
      params.delete("new");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  const create = async () => {
    if (!form.name.trim()) { toast.error("Project name is required"); return; }
    setCreating(true);
    try {
      const res = await api.post("/projects", form);
      toast.success("Project created");
      navigate(`/projects/${res.data.id}`);
    } catch (e) {
      toast.error("Failed to create project");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this project?")) return;
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  return (
    <Layout>
      <header className="h-16 flex items-center justify-between px-6 border-b border-slate-200 bg-white/70 backdrop-blur-xl z-20 sticky top-0">
        <h1 className="font-display text-xl font-bold text-slate-900">Consulting Projects</h1>
        <button
          data-testid="new-project-btn"
          onClick={() => setOpen(true)}
          className="bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-md font-medium transition-colors flex items-center gap-2 text-sm"
        >
          <Plus className="h-4 w-4" /> New Project
        </button>
      </header>

      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <p className="text-slate-400 text-sm">Loading…</p>
        ) : projects.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg p-12 text-center">
            <FolderKanban className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">No projects yet</p>
            <button
              onClick={() => setOpen(true)}
              data-testid="empty-create-btn"
              className="mt-5 bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-md font-medium text-sm inline-flex items-center gap-2"
            >
              <Plus className="h-4 w-4" /> New Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p) => (
              <div
                key={p.id}
                data-testid={`project-row-${p.id}`}
                onClick={() => navigate(`/projects/${p.id}`)}
                className="cursor-pointer bg-white border border-slate-200 rounded-lg p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === "completed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {p.status}
                  </span>
                  <button data-testid={`delete-project-${p.id}`} onClick={(e) => remove(p.id, e)} className="text-slate-300 hover:text-red-500 transition-colors">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <h4 className="font-display font-semibold text-slate-900 mt-3 text-lg">{p.name}</h4>
                <p className="text-sm text-slate-500 line-clamp-2 mt-1 min-h-[2.5rem]">{p.description || "No description"}</p>
                <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
                  <span>Phase {p.current_phase}/6</span>
                  <span className="text-emerald-600 font-medium flex items-center gap-1 group-hover:gap-2 transition-all">Open <ArrowRight className="h-3.5 w-3.5" /></span>
                </div>
                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${(p.current_phase / 6) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">New Consulting Project</DialogTitle>
            <DialogDescription>Name your engagement and pick a framework template to begin the 6-phase HPI workflow.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Project Name</label>
              <input
                data-testid="project-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Q3 Sales Enablement"
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
              <textarea
                data-testid="project-desc-input"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none resize-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Start from a Framework Template</label>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    data-testid={`template-option-${t.id}`}
                    onClick={() => setForm({ ...form, template_id: t.id })}
                    className={`text-left border rounded-md p-3 transition-colors ${form.template_id === t.id ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-800 text-sm">{t.name}</span>
                      <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-600">{t.category}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">{t.description}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-md text-slate-600 hover:bg-slate-100 text-sm font-medium">Cancel</button>
            <button
              data-testid="create-project-submit"
              onClick={create}
              disabled={creating}
              className="bg-emerald-500 text-white hover:bg-emerald-600 px-4 py-2 rounded-md font-medium text-sm disabled:opacity-60"
            >
              {creating ? "Creating…" : "Create Project"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
